import { stText } from "../i18n/streamingPagesPresentation";
/**
 * useJobStream — SSE + relay fallback + polling for console modals.
 *
 * Used by the 4 SSE modals: QbMaintenance, AptRunner, PackageJob, SignalInstall.
 * NOT used by DashboardUpdate, AppUpdate, or VpnJob (they have their own hooks).
 *
 * SSE dedup: On reconnect, the backend replays all existing lines via the `init`
 * event's `existingLines` count. This hook tracks how many lines were received
 * and skips batch-replayed lines on reconnect.
 *
 * Relay fallback (optional):
 *   When relayStreamUrl + relayStatusUrl are provided, a primary SSE disconnect
 *   during a non-terminal job opens the relay SSE instead of falling to polling.
 *   Health polling probes /health every 2s; once 200 is returned the hook updates
 *   the banner copy to "restart complete" but stays on relay (no primary reconnect).
 *   If the relay also disconnects, the hook falls back to status polling as before.
 *   Sequential only — primary and relay are never open simultaneously.
 *
 *   Relay URLs are held in refs (not closures) so they can be updated after
 *   connect() is called — this matters for update_quickbox where the jobId is
 *   known only after the POST succeeds but before primary SSE disconnects.
 */
import { useCallback, useEffect, useRef, useState, useMemo } from "react";

type JobStreamConfig = {
  /** Primary SSE stream URL (e.g. /api/admin/qb/stream) */
  streamUrl: string;
  /** Polling fallback URL (e.g. /api/admin/qb/status) */
  statusUrl: string;
  /** Polling interval in ms (default: 3000) */
  pollIntervalMs?: number;
  /** Extract job data from poll response (default: data.job) */
  extractJob?: (data: Record<string, unknown>) => Record<string, unknown> | null;
  /** Terminal status values (default: ["completed", "failed"]) */
  terminalStatuses?: string[];
  /**
   * Relay SSE URL — opened when primary disconnects during an active job.
   * Typically /api/admin/qb/relay/stream?jobId=<id>.
   * Requires relayStatusUrl.
   */
  relayStreamUrl?: string;
  /**
   * Relay polling URL — polled when relay SSE also disconnects.
   * Typically /api/admin/qb/relay/status?jobId=<id>.
   */
  relayStatusUrl?: string;
};

type StreamSource = "primary" | "relay";

type ProgressState = {
  step: number;
  total: number;
  percent: number;
  label: string;
} | null;

type JobStreamState = {
  lines: string[];
  /** Opaque source metadata aligned with lines; consumers decide whether to render it. */
  linePresentations: unknown[];
  status: string;
  exitCode: number | null;
  durationMs: number | null;
  /** null = no error; non-null = informational relay message or terminal error */
  error: string | null;
  errorCode?: "streamPolling" | "streamRelay" | "streamRestarted" | null;
  /** Which SSE source is currently active */
  streamSource: StreamSource;
  /**
   * Epoch ms of the first connect() call — used for the count-up elapsed timer.
   * Null when no job is active.
   */
  connectedAt: number | null;
  /**
   * Epoch ms of the last received line, keepalive, or status event.
   * Used for the "last activity X ago" indicator.
   * Null when no job is active.
   */
  lastActivityAt: number | null;
  /**
   * Most-recently received `event: progress` payload.
   * Null until the first progress event arrives.
   */
  progress: ProgressState;
};

type UseJobStreamReturn = JobStreamState & {
  connect: () => void;
  disconnect: () => void;
  reset: () => void;
  isTerminal: boolean;
  /**
   * Seconds elapsed since connect() was called (count-up, updates every 1s).
   * 0 when no job is active.
   */
  elapsedSec: number;
  /**
   * Seconds since the last received line/keepalive/status event.
   * null when no job is active.
   */
  lastActivitySec: number | null;
};

export type { ProgressState };

const DEFAULT_POLL_MS = 3000;
const HEALTH_POLL_MS = 2000;
const HEALTH_URL = "/health";

export const useJobStream = (config: JobStreamConfig): UseJobStreamReturn => {
  const {
    streamUrl,
    statusUrl,
    pollIntervalMs = DEFAULT_POLL_MS,
    extractJob = (d) => (d.job as Record<string, unknown> | null),
    terminalStatuses = ["completed", "failed"],
    relayStreamUrl,
    relayStatusUrl,
  } = config;

  const [state, setState] = useState<JobStreamState>({
    lines: [], linePresentations: [],
    status: "idle",
    exitCode: null,
    durationMs: null,
    error: null, errorCode: null,
    streamSource: "primary",
    connectedAt: null,
    lastActivityAt: null,
    progress: null,
  });

  // Keep relay URLs in refs so openRelayStream always sees the latest values
  // regardless of when connect() was called.
  const relayStreamUrlRef = useRef(relayStreamUrl);
  const relayStatusUrlRef = useRef(relayStatusUrl);
  relayStreamUrlRef.current = relayStreamUrl;
  relayStatusUrlRef.current = relayStatusUrl;

  const streamUrlRef = useRef(streamUrl);
  const statusUrlRef = useRef(statusUrl);
  streamUrlRef.current = streamUrl;
  statusUrlRef.current = statusUrl;

  const eventSourceRef = useRef<EventSource | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const healthTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statusRef = useRef(state.status);
  const terminalStatusesRef = useRef(terminalStatuses);
  terminalStatusesRef.current = terminalStatuses;
  // Mirror lastActivityAt in a ref so onRelayError can read it without a
  // stale closure.  setState batching means we cannot read state.lastActivityAt
  // directly inside a callback created during the relay-open call.
  const lastActivityAtRef = useRef<number | null>(null);

  // Guards the one-shot relay auto-reconnect.  Set to true after the first
  // relay drop triggers a 2s delayed reopen.  Reset in connect() so each
  // fresh dispatch gets one reconnect opportunity.  Without this guard,
  // openRelayStream calling itself recursively on every drop would be infinite.
  const relayReconnectAttemptedRef = useRef(false);

  // Stable ref to the latest openRelayStream callback — used inside the
  // 2s setTimeout so the recursive call always has the most-recent closure
  // without adding openRelayStream to its own useCallback deps (circular).
  const openRelayStreamRef = useRef<(() => void) | null>(null);

  const existingLinesCountRef = useRef(0);
  const receivedCountRef = useRef(0);
  statusRef.current = state.status;

  // Elapsed / last-activity tick timer
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [tickCount, setTickCount] = useState(0);

  const isTerminal = terminalStatuses.includes(state.status);

  // Derived elapsed + lastActivity values — recomputed on every tick
  const elapsedSec = useMemo(() => {
    if (!state.connectedAt) return 0;
    return Math.floor((Date.now() - state.connectedAt) / 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.connectedAt, tickCount]);

  const lastActivitySec = useMemo(() => {
    if (!state.lastActivityAt) return null;
    return Math.floor((Date.now() - state.lastActivityAt) / 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.lastActivityAt, tickCount]);

  // ─── Timers ─────────────────────────────────────────────────────────────────

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const stopHealthPolling = useCallback(() => {
    if (healthTimerRef.current) {
      clearInterval(healthTimerRef.current);
      healthTimerRef.current = null;
    }
  }, []);

  const startTickTimer = useCallback(() => {
    if (tickTimerRef.current) return;
    tickTimerRef.current = setInterval(() => {
      setTickCount((n) => n + 1);
    }, 1000);
  }, []);

  const stopTickTimer = useCallback(() => {
    if (tickTimerRef.current) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
  }, []);

  /** Record a new line/keepalive/status event as "activity now". */
  const touchActivity = useCallback(() => {
    const now = Date.now();
    lastActivityAtRef.current = now;
    setState((prev) => ({ ...prev, lastActivityAt: now }));
  }, []);

  // ─── Terminal ────────────────────────────────────────────────────────────────

  const handleTerminal = useCallback((termStatus: string, code: number | null, duration: number | null) => {
    setState((prev) => ({
      ...prev,
      status: termStatus,
      exitCode: code,
      durationMs: duration,
      error: null, errorCode: null,
    }));
    stopPolling();
    stopHealthPolling();
    stopTickTimer();
  }, [stopPolling, stopHealthPolling, stopTickTimer]);

  // ─── Status polling ──────────────────────────────────────────────────────────

  const startPollingFallback = useCallback((url: string) => {
    if (pollTimerRef.current) return;

    const poll = async () => {
      if (terminalStatusesRef.current.includes(statusRef.current)) {
        stopPolling();
        return;
      }

      try {
        const res = await fetch(url, { credentials: "include", cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json() as Record<string, unknown>;
        if (data.ok !== true) return;

        const job = extractJob(data);
        if (!job) {
          // When the endpoint returns ok:true but no job object, check whether
          // the top-level status indicates the job is still pending/idle (e.g.
          // the relay's state file hasn't appeared yet during a restart window).
          // In that case, skip this tick and retry — do NOT declare failure.
          // Only fall through to handleTerminal when no in-progress signal exists.
          const topStatus = typeof data.status === "string" ? data.status : null;
          if (topStatus && !terminalStatusesRef.current.includes(topStatus)) {
            // "idle" or "running" at top level → relay/server is alive but job
            // state file is not visible yet. Retry next interval.
            return;
          }
          handleTerminal("failed", null, null);
          return;
        }

        const jobStatus = job.status as string;

        // Polling is for STATUS DETECTION ONLY.  Do NOT append lastLines.
        //
        // The admin endpoint's currentJob.output is populated by journalctl tail
        // (outer transient unit journal).  script -fq's pty output causes journald
        // to split lines at chunk boundaries, producing truncated entries like
        // "[qb-service-setup]" alone (the rest of the line lands as a separate
        // journal entry that may already be in state.lines from SSE).  Appending
        // these journal-sourced entries here injects dirty / truncated lines at the
        // end of the modal after the clean relay stream has finished.
        //
        // The relay's tail of the V4 log file is the authoritative line source:
        // it carries the same mflibs::log output without pty chunk-boundary splits.
        // By the time polling has engaged, SSE has already delivered the real
        // content.  We still call touchActivity() when lastLines is present so the
        // "last activity" timer reflects that the job is still progressing.
        const hasLastLines = Array.isArray(job.lastLines) && (job.lastLines as string[]).length > 0;
        if (hasLastLines) {
          touchActivity();
        }

        if (terminalStatusesRef.current.includes(jobStatus)) {
          handleTerminal(
            jobStatus,
            typeof job.exitCode === "number" ? job.exitCode : null,
            typeof job.durationMs === "number" ? job.durationMs : null,
          );
        }
      } catch {
        // Non-fatal, retry next interval
      }
    };

    void poll();
    pollTimerRef.current = setInterval(() => void poll(), pollIntervalMs);
  }, [pollIntervalMs, extractJob, handleTerminal, stopPolling, touchActivity]);

  // ─── SSE wire-up (shared between primary and relay) ─────────────────────────

  /**
   * Attach the three named SSE event handlers to an EventSource.
   * onError fires after the EventSource has been closed by the browser.
   */
  const wireEventSource = useCallback((es: EventSource, source: StreamSource, onError: () => void) => {
    es.addEventListener("init", (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data as string) as { existingLines?: number; status?: string };
        existingLinesCountRef.current = parsed.existingLines ?? 0;
        receivedCountRef.current = 0;
        touchActivity();
        // The server reports the job's live status in `init`; a terminal value
        // is delivered by the `status` event that follows, with exit code and duration.
        const initStatus = parsed.status;
        if (typeof initStatus === "string" && !terminalStatusesRef.current.includes(initStatus)) {
          setState((prev) => (prev.status === "idle" ? { ...prev, status: initStatus } : prev));
        }
      } catch { /* ignore */ }
    });

    es.addEventListener("line", (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data as string) as { text: string; textPresentation?: unknown };
        receivedCountRef.current += 1;
        touchActivity();

        // Always append.  The count-based dedup previously here was incorrect
        // for the primary→relay transition: primary streams pre-dashboard
        // pipeline output (devpull, apt, dependencies, …) that the relay's v4
        // log file does NOT carry, so comparing relay's receivedCount against
        // state.lines.length skips relay's early dashboard-step content as if
        // it were duplicates of primary's pre-dashboard lines.  The cost of
        // dropping the dedup is at most a few v3 mflibs::log lines that land
        // in both primary's journal and the v4 log file (e.g. "v4-dashboard
        // update is running — streaming live output below…") appearing twice
        // around the transition.  That's much cheaper than losing entire
        // dashboard-step phases.
        // Output is flowing, so the job is running even when `init` carried no status.
        setState((prev) => ({
          ...prev,
          status: prev.status === "idle" ? "running" : prev.status,
          lines: [...prev.lines, parsed.text],
          linePresentations: [...prev.linePresentations, parsed.textPresentation ?? null],
        }));
      } catch { /* ignore */ }
    });

    es.addEventListener("status", (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data as string) as {
          status: string;
          exitCode?: number | null;
          durationMs?: number | null;
        };
        touchActivity();
        if (terminalStatusesRef.current.includes(parsed.status)) {
          es.close();
          if (eventSourceRef.current === es) eventSourceRef.current = null;
          stopHealthPolling();
          handleTerminal(parsed.status, parsed.exitCode ?? null, parsed.durationMs ?? null);
        } else {
          setState((prev) => ({ ...prev, status: parsed.status }));
        }
      } catch { /* ignore */ }
    });

    // event: progress — phase progress marker emitted during dashboard update phase
    es.addEventListener("progress", (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data as string) as {
          step: number;
          total: number;
          label: string;
          percent: number;
        };
        touchActivity();
        setState((prev) => ({
          ...prev,
          progress: {
            step: parsed.step,
            total: parsed.total,
            percent: parsed.percent,
            label: parsed.label,
          },
        }));
      } catch { /* ignore */ }
    });

    // event: complete — terminal event from the new SSE contract
    es.addEventListener("complete", (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data as string) as { exitCode: number };
        es.close();
        if (eventSourceRef.current === es) eventSourceRef.current = null;
        stopHealthPolling();
        handleTerminal(parsed.exitCode === 0 ? "completed" : "failed", parsed.exitCode, null);
      } catch { /* ignore */ }
    });

    es.onerror = () => {
      // If the complete handler already cleared this EventSource (terminal
      // event arrived cleanly), don't fire onError — the connection close is
      // EXPECTED.  Without this guard, the browser's automatic onerror after
      // res.end() fires `onRelayError`, which sets the "Stream connection
      // lost. Polling for status..." banner and starts polling — both visible
      // long after the modal has already moved to its terminal state.
      const alreadyClosed = eventSourceRef.current !== es;
      es.close();
      if (eventSourceRef.current === es) eventSourceRef.current = null;
      if (alreadyClosed) return;
      onError();
    };

    setState((prev) => ({ ...prev, streamSource: source }));
  }, [handleTerminal, stopHealthPolling, touchActivity]);

  // ─── Disconnect ──────────────────────────────────────────────────────────────

  const disconnect = useCallback(() => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    stopPolling();
    stopHealthPolling();
  }, [stopPolling, stopHealthPolling]);

  // ─── Relay + health-poll ─────────────────────────────────────────────────────

  /**
   * Open the relay SSE. Called imperatively from the primary onerror handler.
   * Reads relay URLs from refs so the latest config values are always used
   * regardless of when connect() was originally called.
   */
  const openRelayStream = useCallback(() => {
    const relayStream = relayStreamUrlRef.current;
    const relayStatus = relayStatusUrlRef.current;

    if (!relayStream || !relayStatus) {
      // No relay configured — fall straight to primary status polling
      setState((prev) => ({
        ...prev,
        error: stText("streamConnectionLostPollingForStatus"),
        errorCode: "streamPolling",
        streamSource: "primary",
      }));
      startPollingFallback(statusUrlRef.current);
      return;
    }

    const es = new EventSource(relayStream, { withCredentials: true });
    eventSourceRef.current = es;

    const onRelayError = () => {
      // Do NOT stop health polling here — keep it running so the banner
      // can still transition to "restart complete" even if the relay drops.
      if (terminalStatusesRef.current.includes(statusRef.current)) return;

      // ── One-shot relay auto-reconnect ────────────────────────────────────────
      //
      // The install pipeline restarts v4-update-relay.service at step 17.6
      // (after the new release's `current/` symlink flip, before the heavy
      // post-dashboard work).  This causes ~1s of relay downtime mid-stream.
      // On the FIRST relay drop we schedule a silent reopen after 2s rather
      // than falling straight to polling.  If the reopen succeeds, the stream
      // resumes with no visible disruption.  If it drops again, the ref guard
      // below directs us straight to the existing debounce + polling path.
      if (!relayReconnectAttemptedRef.current) {
        relayReconnectAttemptedRef.current = true;
        // Silent — do not change the banner or streamSource during the 2s
        // window.  The user continues to see "Dashboard is restarting…" or
        // the health-polled "restart complete" copy unchanged.
        setTimeout(() => {
          // Re-open only if still non-terminal (job may have finished during
          // the 2s wait via the terminal event on the briefly-open EventSource,
          // or via a completed event before the relay bounced).
          // Use openRelayStreamRef so we always invoke the latest closure
          // without creating a circular useCallback dependency.
          if (!terminalStatusesRef.current.includes(statusRef.current)) {
            openRelayStreamRef.current?.();
          }
        }, 2000);
        return;
      }

      // ── Fallback: both primary AND relay failed ──────────────────────────────
      //
      // Debounce: if the relay had recent activity (within 3s), suppress the
      // "Stream connection lost" banner.  During a clean job-end the sequence
      // is: last log line → event:complete → socket close.  The complete
      // handler closes eventSourceRef BEFORE onerror fires, so `alreadyClosed`
      // in wireEventSource catches it and never calls onRelayError at all.
      // But for an SSE flush race the socket can close a split-second after
      // the last line without the complete event arriving first.  In that case
      // lastActivityAtRef will be very recent, so we suppress the banner and
      // let polling confirm the terminal state silently.
      const recentActivity =
        lastActivityAtRef.current !== null &&
        Date.now() - lastActivityAtRef.current < 3000;

      if (recentActivity) {
        // Silent fallback — don't alarm the user; event:complete may arrive
        // via the polling tick within seconds.
        setState((prev) => ({ ...prev, streamSource: "primary" }));
      } else {
        // No recent activity — something genuinely went wrong.  Show the
        // "connection lost" banner so the user knows we're degraded.
        setState((prev) => ({
          ...prev,
          error: stText("streamConnectionLostPollingForStatus"),
          errorCode: "streamPolling",
          streamSource: "primary",
        }));
      }

      // Poll the PRIMARY status endpoint (v4's /api/admin/qb/status) — it
      // holds the in-memory currentJob with the finalized terminal state after
      // recovery.  The relay's /status only returns {status:"idle"} once v4
      // deletes the job state file, so we would never see the terminal state
      // if we polled relay.
      startPollingFallback(statusUrlRef.current);
    };

    wireEventSource(es, "relay", onRelayError);

    // Show relay-active banner (not "lost") while relay is streaming
    setState((prev) => ({
      ...prev,
      error: stText("dashboardIsRestartingStreamingContinuesViaRelay"),
      errorCode: "streamRelay",
      streamSource: "relay",
    }));

    // Once we are on relay we STAY on relay until the job reaches a terminal
    // state.  Previously we health-polled v4 and re-opened a primary SSE the
    // moment /healthz responded, which produced two concurrent streams (relay
    // tailing the v4 log file + a fresh primary tailing the outer unit's
    // journal).  Both streams carry the same post-dashboard pipeline lines but
    // delivered them at different paces, producing interleaved / out-of-order
    // output in the modal.  Relay alone has all the content we need after the
    // dashboard restart — pipeline-X writes via mflibs::log land in the same
    // v4 log file the relay tails, and pipeline-finalize's `[QB_DONE]` fires
    // `event: complete` cleanly.  No primary reconnect needed.
    //
    // BANNER TRANSITION: We DO still health-poll /health so we can update the
    // banner copy when v4 finishes restarting.  The "Dashboard is restarting"
    // message is correct for the first ~30s, but once /health returns 200 the
    // dashboard is back — the remaining wait is v3's post-dashboard pipeline
    // work flowing through the relay tail.  We flip the banner to
    // "Dashboard restart complete — finishing post-update work..." so the user
    // knows the modal is still active and waiting on something real, not stuck.
    // The health poll is BANNER-ONLY and does NOT reconnect primary SSE.
    if (healthTimerRef.current) {
      clearInterval(healthTimerRef.current);
    }
    let bannerTransitioned = false;
    healthTimerRef.current = setInterval(() => {
      if (bannerTransitioned) {
        // One-shot: stop polling once we have updated the banner.
        stopHealthPolling();
        return;
      }
      // Stop probing if the job already reached a terminal state.
      if (terminalStatusesRef.current.includes(statusRef.current)) {
        stopHealthPolling();
        return;
      }
      void fetch(HEALTH_URL, { credentials: "include", cache: "no-store" })
        .then((res) => {
          if (res.ok && !bannerTransitioned) {
            bannerTransitioned = true;
            stopHealthPolling();
            // Only update banner if still in relay mode (not yet terminal).
            if (!terminalStatusesRef.current.includes(statusRef.current)) {
              setState((prev) => ({
                ...prev,
                error: stText("dashboardRestartCompleteFinishingPostUpdateWork"),
                errorCode: "streamRestarted",
              }));
            }
          }
        })
        .catch(() => { /* not yet up — retry next interval */ });
    }, HEALTH_POLL_MS);
  }, [wireEventSource, startPollingFallback, stopHealthPolling]);

  // Keep the stable ref current so the 2s retry setTimeout always calls the
  // latest closure.  This assignment is synchronous (happens during render
  // before any effects), so the ref is never stale when the timeout fires.
  openRelayStreamRef.current = openRelayStream;

  // ─── Primary connect ─────────────────────────────────────────────────────────

  const connect = useCallback(() => {
    disconnect();
    existingLinesCountRef.current = 0;
    receivedCountRef.current = 0;
    relayReconnectAttemptedRef.current = false;

    // Record connection time and kick off the 1s elapsed timer
    const now = Date.now();
    setState((prev) => ({ ...prev, connectedAt: now, lastActivityAt: now }));
    startTickTimer();

    const es = new EventSource(streamUrlRef.current, { withCredentials: true });
    eventSourceRef.current = es;

    const onPrimaryError = () => {
      if (!terminalStatusesRef.current.includes(statusRef.current)) {
        openRelayStream();
      }
    };

    wireEventSource(es, "primary", onPrimaryError);
  }, [disconnect, wireEventSource, openRelayStream, startTickTimer]);

  // ─── Reset ───────────────────────────────────────────────────────────────────

  const reset = useCallback(() => {
    disconnect();
    stopTickTimer();
    existingLinesCountRef.current = 0;
    receivedCountRef.current = 0;
    setTickCount(0);
    setState({
      lines: [], linePresentations: [],
      status: "idle",
      exitCode: null,
      durationMs: null,
      error: null, errorCode: null,
      streamSource: "primary",
      connectedAt: null,
      lastActivityAt: null,
      progress: null,
    });
  }, [disconnect, stopTickTimer]);

  // ─── Cleanup on unmount ──────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      eventSourceRef.current?.close();
      stopPolling();
      stopHealthPolling();
      stopTickTimer();
    };
  }, [stopPolling, stopHealthPolling, stopTickTimer]);

  return {
    ...state,
    connect,
    disconnect,
    reset,
    isTerminal,
    elapsedSec,
    lastActivitySec,
  };
};
